import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import ConfirmDialog, { IConfirmDialogResult } from '../../Shared/ConfirmDialog';
import {
  IDispatchProps, IProps, IStateProps,
} from './interfaces';
import LiveAssetModal from '../Modals/LiveAssetModal/component';
import { IAppState } from '../../../state/reducers';
import { deleteSelectedAssets, terminateLiveStream } from '../../../state/deleteAssets';
import {
  closeNewAssetModal, openAssetModal, recoverLiveStream, setDeleteConfirmDialogOpen,
  setRecoverConfirmDialogOpen, setStopConfirmDialogOpen,
  setTerminateConfirmDialogOpen, updateLiveAssetModalProperties,
} from '../../../state/liveEvents/liveAssetModal';
import { searchLiveAssets } from '../../../state/liveEvents/liveEventsList';
import { editAssetMetadata } from '../../AssetDetails/api/editAssetMetadata';

const LiveEventModals: React.FC<IProps> = (props: IProps) => {
  const {
    selectedAsset,
    refreshAssets, deleteSelectedAssets, terminateLiveStream, closeNewAssetModal,
    setDeleteConfirmDialogOpen, setStopConfirmDialogOpen, setTerminateConfirmDialogOpen,
    setRecoverConfirmDialogOpen, recoverLiveStream,
    isOpened, extendMode, restartMode,
    deleteConfirmDialogOpen, stopConfirmDialogOpen, terminateConfirmDialogOpen,
    recoverConfirmDialogOpen,
  } = props;

  const close = () => {
    closeNewAssetModal();
  };

  const onDeleteDialogResult = (result: IConfirmDialogResult) => {
    if (result.confirmed) {
      (async () => {
        await deleteSelectedAssets({ ids: [selectedAsset._id] }, false, refreshAssets);
      })();
    }

    setDeleteConfirmDialogOpen(false, null);
  };

  const onStopDialogResult = (result: IConfirmDialogResult) => {
    (async () => {
      if (result.confirmed) {
        selectedAsset._source.stop = moment().format();
        const editedAsset = { ...selectedAsset._source, _id: selectedAsset._id };
        await editAssetMetadata(editedAsset);

        // Delaying the list refresh to give the Edit time to complete.
        // Ideally we should be listening for dispatches and reacting.
        // VA-1136 may address this issue in the future.
        setTimeout(() => refreshAssets(), 1000);
      }
    })();

    setStopConfirmDialogOpen(false, null);
  };

  const onTerminateDialogResult = (result: IConfirmDialogResult) => {
    (async () => {
      if (result.confirmed) {
        await terminateLiveStream(selectedAsset._id, refreshAssets);

        setTimeout(() => refreshAssets(), 1000);
      }
    })();

    setTerminateConfirmDialogOpen(false, null);
  };

  const onRecoverDialogResult = (result: IConfirmDialogResult) => {
    (async () => {
      if (result.confirmed) {
        await recoverLiveStream(selectedAsset._id, refreshAssets);

        setTimeout(() => refreshAssets(), 1000);
      }
    })();

    setRecoverConfirmDialogOpen(false, null);
  };

  return (
    <>
      <ConfirmDialog
        open={deleteConfirmDialogOpen}
        title={selectedAsset ? `Confirm delete: ${selectedAsset._source.title}` : 'Confirm delete'}
        message="Are you sure you wish to proceed with the delete?"
        onResult={onDeleteDialogResult}
      />
      <ConfirmDialog
        open={stopConfirmDialogOpen}
        title={selectedAsset ? `Confirm stop: ${selectedAsset._source.title}` : 'Confirm stop'}
        message="Are you sure you wish to proceed with the stop?"
        onResult={onStopDialogResult}
      />
      <ConfirmDialog
        open={terminateConfirmDialogOpen}
        title={selectedAsset ? `Confirm terminate: ${selectedAsset._source.title}` : 'Confirm terminate'}
        message="Are you sure you wish to proceed with the terminate?"
        onResult={onTerminateDialogResult}
      />
      <ConfirmDialog
        open={recoverConfirmDialogOpen}
        title={selectedAsset ? `Recover video: ${selectedAsset._source.title}` : 'Recover video'}
        message="This rebuilds the video from the recorded stream and turns this event into a normal video asset. The live stream cannot be resumed afterwards. Continue?"
        onResult={onRecoverDialogResult}
      />
      <LiveAssetModal
        asset={selectedAsset}
        isOpen={isOpened}
        extendMode={extendMode}
        restartMode={restartMode}
        refresh={refreshAssets}
        closeModal={close}
      />
    </>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpened: state.liveAssetModal.isOpened,
  extendMode: state.liveAssetModal.extendMode,
  restartMode: state.liveAssetModal.restartMode,
  selectedAsset: state.liveAssetModal.selectedAsset,
  deleteConfirmDialogOpen: state.liveAssetModal.deleteConfirmDialogOpen,
  stopConfirmDialogOpen: state.liveAssetModal.stopConfirmDialogOpen,
  terminateConfirmDialogOpen: state.liveAssetModal.terminateConfirmDialogOpen,
  recoverConfirmDialogOpen: state.liveAssetModal.recoverConfirmDialogOpen,
});

const mapDispatchToProps: IDispatchProps = {
  openAssetModal,
  deleteSelectedAssets,
  terminateLiveStream,
  updateLiveAssetModalProperties,
  closeNewAssetModal,
  setDeleteConfirmDialogOpen,
  setStopConfirmDialogOpen,
  setTerminateConfirmDialogOpen,
  setRecoverConfirmDialogOpen,
  recoverLiveStream,
  refreshAssets: searchLiveAssets,
};

export default connect(mapStateToProps, mapDispatchToProps)(LiveEventModals);
